/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';

import { IDlvrySplitHeroBlockData } from './dlvry-split-hero-block.data';
import { IDlvrySplitHeroBlockProps } from './dlvry-split-hero-block.props.autogenerated';

import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent, Text } from '@msdyn365-commerce/core';

export interface IDlvrySplitHeroBlockViewProps extends IDlvrySplitHeroBlockProps<IDlvrySplitHeroBlockData> {
    SplitHeroBlock: IModuleProps;
    baseClassName: string;
    heading: JSX.Element | null;
    description: JSX.Element | null;
    links: JSX.Element | null;
    image: JSX.Element | null;
}

/**
 *
 * DlvrySplitHeroBlock component
 * @extends {React.PureComponent<IDlvrySplitHeroBlockProps<IDlvrySplitHeroBlockData>>}
 */
class DlvrySplitHeroBlock extends React.PureComponent<IDlvrySplitHeroBlockProps<IDlvrySplitHeroBlockData>> {

    private baseClassName = 'dlvry-split-hero-block';

    public render(): JSX.Element | null {
        const viewProps: IDlvrySplitHeroBlockViewProps = {
            ...this.props,
            SplitHeroBlock: {
                className: this.baseClassName,
                moduleProps: this.props
            },
            baseClassName: this.baseClassName,
            heading: this.renderHeading(),
            description: this.renderDescription(),
            links: this.renderLinks(),
            image: this.renderImage()
        };
        return this.props.renderView(viewProps);
    }

    private renderHeading(): JSX.Element | null {
        const { config, context } = this.props;

        if (!config.heading) {
            return null;
        }

        return (
            <Text
                className={`${this.baseClassName}__heading`}
                tag={config.heading.tag || 'h2'}
                text={config.heading.text}
                editProps={{ onEdit: (event) => config.heading!.text = event.target.value, requestContext: context.request }}
            >
                {config.heading.text}
            </Text>
        );
    }

    private renderDescription(): JSX.Element | null {
        const { config, context } = this.props;

        return (
            <RichTextComponent
                className={`${this.baseClassName}__description`}
                text={config.description}
                editProps={{ onEdit: (event) => config.description = event.target.value, requestContext: context.request }}
            />
        );
    }

    private renderLinks(): JSX.Element | null {
        const { config } = this.props;

        if (!config.links || config.links.length === 0) {
            return null;
        }

        return (
            <div className={`${this.baseClassName}__links`}>
                {config.links.map((link, index) => (
                    <a
                        key={index}
                        href={link.linkUrl.destinationUrl}
                        aria-label={link.ariaLabel}
                        target={link.openInNewTab ? '_blank' : '_self'}
                        rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                        className={classnames(`${this.baseClassName}__link`, { 'primary': link.primary })}
                    >
                        {link.linkText}
                    </a>
                ))}
            </div>
        );
    }

    private renderImage(): JSX.Element | null {
        const { config } = this.props;

        if (!config.image || !config.image.src) {
            return null;
        }

        return (
            <img
                className={`${this.baseClassName}__image`}
                src={config.image.src}
                alt={config.image.altText || ''}
            />
        );
    }
}

export default DlvrySplitHeroBlock;
